import { Box, Button, Typography } from '@mui/material';
import { Base } from 'components/Containers/BaseContainer';
import { ToolbarContainer } from 'components/Containers/ToolbarContainer';
import SWTab from 'components/Layout/Tabs/SWTab';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import { useEffect, useMemo, useState } from 'react';
import { TabList, TabPanel, Tabs } from 'react-tabs';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { resetSquaresDashboard } from 'store/SquaresStore/DashboardSquaresState.slice';
import LoadingDots from 'views/FleetOverview/components/AssetCardsV2/LoadingDots';
import ComponentsCostBreakdown from './Components/ComponentsCostBreakdown';
import MaintenanceBreakdown from './Components/MaintenanceBreakdown';
import MaintenanceComparison from './Components/MaintenanceComparison';
import SelectAllCheckbox from './Components/SelectAllCheckbox';
import TotalCostVSLabourCost from './Components/TotalCostVSLabourCost';
import WorkOrderStatusBreakdown from './Components/WorkOrderBreakdown';
import { extractWorkOrderData, getFirstAttribute } from './utils';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import dayjs from 'dayjs';
import { AssetsTreeChart } from './Components/TreeChart';
import { convertKmsToMiles } from 'utils/helpers/unitSystem';
import { formatTo2dp } from 'utils/helpers/general';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const FilterSection = ({
  setSelectedCell,
  setIsLoading,
  isLoading,
}: {
  setSelectedCell: (param: string[]) => void;
  setIsLoading: (param: boolean) => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const assetsData = useAppSelector(
    (state) => state.squaresDashboardReducer?.assetDashboard?.assets
  );
  const squaresHandler = new SquaresDashboardHandler();
  const formerSelectedDates: any = sessionStorage.getItem(
    'datePickerSelection'
  );

  let initialStartDate = new Date();
  initialStartDate.setFullYear(initialStartDate.getFullYear() - 1);
  let initialEndDate = new Date();
  if (formerSelectedDates) {
    const dateObjs = JSON.parse(formerSelectedDates);
    initialStartDate = new Date(dateObjs.startDate);
    initialEndDate = new Date(dateObjs.endDate);
  }

  const [selectedStartDate, setStartDate] = useState(initialStartDate);
  const [selectedEndDate, setEndDate] = useState(initialEndDate);
  const [selectAllAssets, setSelectAllAssets] = useState<boolean>(false);

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setStartDate(startDate);
    setEndDate(endDate);

    sessionStorage.setItem(
      'datePickerSelection',
      JSON.stringify({
        startDate,
        endDate,
      })
    );
  };

  const handleSelectAllAssets = () => {
    setSelectAllAssets((prev) => {
      const temp = !prev;
      if (temp) {
        if (assetsData) {
          setSelectedCell(Object.keys(assetsData));
        }
      } else {
        if (assetsData) {
          const highestKey = getFirstAttribute(assetsData);
          setSelectedCell(highestKey);
        }
      }
      return temp;
    });
  };

  const fetchData = async () => {
    try {
      if (selectedStartDate && selectedEndDate) {
        // clearing state
        setIsLoading(true);
        dispatch(resetSquaresDashboard());
        setSelectedCell([]);
        setSelectAllAssets(false);

        // api calls
        const [responseAssetsData] = await Promise.all([
          squaresHandler.getDashboardAssetsData(
            selectedStartDate,
            selectedEndDate
          ),
          squaresHandler.getPartsComponentsData(
            selectedStartDate,
            selectedEndDate
          ),
          squaresHandler.getPmVsCmMaintenanceMetrics(
            selectedStartDate,
            selectedEndDate
          ),
        ]);

        const highestKey = getFirstAttribute(responseAssetsData?.assets);
        setSelectedCell(highestKey);
      } else {
        dispatch(
          showNotification({
            message: t('please_select_a_valid_date_range'),
            type: 'error',
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      void fetchData();
    }
  }, [accessToken]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        sx={{
          textAlign: 'center',
          fontSize: '20px',
          color: palette.textPrimary,
        }}
      >
        {' '}
        {t('assets_total_cost')}
      </Typography>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              height: '30px',
              display: 'flex',
              flexDirection: 'row',
              gap: '2px',
              alignItems: 'center',
            }}
          >
            <p
              style={{
                color: palette.textPrimary,
              }}
            >
              {t('date_range_10')}
            </p>
            <CustomDateRangePicker
              onFinalDateChange={handleDateChange}
              startDate={selectedStartDate}
              endDate={selectedEndDate}
              maxDate={dayjs(new Date().getTime())}
              autoOpenEnabled={false}
            />
          </div>

          <Button
            sx={{
              color: '#FFFFFF',
              border: '1px solid #2F445E',
              backgroundColor: palette.primary,
              fontSize: '12px',
              textTransform: 'none',
              height: '30px',
              '&:hover': {
                color: enableDarkTheme ? 'white' : 'black',
                border: enableDarkTheme ? '1px solid white' : '1px solid black',
              },
            }}
            disabled={isLoading}
            onClick={fetchData}
          >
            {t('apply')}
          </Button>
        </div>

        {/* select all assets checkbox */}
        <div>
          <SelectAllCheckbox
            selectAll={selectAllAssets}
            handleSelectAll={handleSelectAllAssets}
            disabled={isLoading || Object.keys(assetsData)?.length === 0}
          />
        </div>
      </div>
    </div>
  );
};

const MaintenanceComparisonTab = ({
  selectedCell,
}: {
  selectedCell: string[];
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const maintenanceMetricsData = useAppSelector(
    (state) => state.squaresDashboardReducer?.maintenanceMetrics
  );
  return (
    <MaintenanceComparison
      enableDarkTheme={enableDarkTheme}
      data={maintenanceMetricsData}
      selectedCell={selectedCell}
    />
  );
};

const MaintenanceBreakdownTab = ({
  selectedCell,
}: {
  selectedCell: string[];
}) => {
  const maintenanceMetricsData = useAppSelector(
    (state) => state.squaresDashboardReducer?.maintenanceMetrics
  );
  return (
    <MaintenanceBreakdown
      data={maintenanceMetricsData}
      selectedCell={selectedCell}
    />
  );
};

const WorkOrderStatusBreakdownTab = ({
  selectedCell,
}: {
  selectedCell: string[];
}) => {
  const maintenanceMetricsData = useAppSelector(
    (state) => state.squaresDashboardReducer?.maintenanceMetrics
  );
  return (
    <WorkOrderStatusBreakdown
      data={maintenanceMetricsData}
      selectedCell={selectedCell}
    />
  );
};

const FallbackForNoSelection = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <Typography
      sx={{
        textAlign: 'center',
        fontSize: '16px',
        color: palette.textPrimary,
      }}
    >
      {t('please_select_a_component_from_the_chart_to_view_details')}
    </Typography>
  );
};

export const AssetsViewV2 = ({
  showBackButton,
  setTabIndex,
}: {
  showBackButton: boolean;
  setTabIndex?: any;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { assetDashboard: assetDashboardData } = useAppSelector(
    (state) => state.squaresDashboardReducer
  );

  // state
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedCell, setSelectedCell] = useState<string[]>([]);

  const handleTreeCellClick = (name: string) => {
    let newSelectedCell;

    if (selectedCell.length === 1 && selectedCell[0] === name) {
      // don't do anything if only one cell is selected
      return;
    }

    if (selectedCell.includes(name)) {
      // Remove name if it's already in the array
      newSelectedCell = selectedCell.filter((cellName) => cellName !== name);
    } else {
      // Add name to the array if it's not already included
      newSelectedCell = [...selectedCell, name];
    }
    setSelectedCell(newSelectedCell);
  };

  const workOrderData = useMemo(() => {
    const tempWorkOrderData = extractWorkOrderData(assetDashboardData.assets);
    // min non-zero totalCost
    const minNonZeroValue = Math.min(
      ...tempWorkOrderData.map((el) =>
        el?.total_cost > 0 ? el?.total_cost : Infinity
      )
    );
    // fallback to zero only if all zero else get a value less than min but greater than 0
    const minDisplayValue =
      minNonZeroValue === Infinity ? 0 : Math.ceil(minNonZeroValue / 10);

    const formattedWorkOrderData = tempWorkOrderData?.map((el) => {
      const totalCost = el?.total_cost || 0;

      // cost per ton
      const costPerTon = el?.total_tonnage
        ? formatTo2dp(totalCost / Number(el.total_tonnage))
        : 0;

      // cost per engine hour
      const costPerEngineHr = el?.total_engine_duration_mins
        ? formatTo2dp(totalCost / (Number(el.total_engine_duration_mins) / 60))
        : 0;

      // convert km to miles
      const totalMiles = el?.total_distance_kms
        ? convertKmsToMiles(el.total_distance_kms)
        : 0;

      // cost per mile
      const costPerMile = el?.total_distance_kms
        ? formatTo2dp(totalCost / Number(totalMiles))
        : 0;
      return {
        ...el,
        totalCostForDisplay:
          el?.total_cost === 0 ? minDisplayValue : el?.total_cost,
        totalCost: el?.total_cost,
        costPerTon,
        costPerEngineHr,
        costPerMile,
      };
    });

    return formattedWorkOrderData;
  }, [assetDashboardData?.assets]);

  return (
    <Box>
      {showBackButton && (
        <Button
          sx={{
            textTransform: 'none',
            border: '1px solid #3666EC',
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
          onClick={() => {
            setTabIndex(0);
          }}
        >
          {t('cdp_button_back')}
        </Button>
      )}
      <FilterSection
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setSelectedCell={setSelectedCell}
      />

      {isLoading ? (
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '16px',
            paddingTop: '10px',
            color: palette.textPrimary,
          }}
        >
          {t('loading_assets_please_wait')}
          <LoadingDots enableDarkTheme={enableDarkTheme} />{' '}
        </Typography>
      ) : (
        <>
          {workOrderData.length <= 0 ? (
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: '20px',
                paddingTop: '10px',
                color: palette.textPrimary,
              }}
            >
              {' '}
              {t('no_data_please_select_a_different_date_range')}
            </Typography>
          ) : (
            <>
              <Base
                sx={{
                  padding: '20px',
                  backgroundColor: enableDarkTheme
                    ? palette.toolBarBackground
                    : palette.toolBarBackground,
                }}
              >
                <Box>
                  <AssetsTreeChart
                    selectedCell={selectedCell}
                    handleCellClick={handleTreeCellClick}
                    data={workOrderData}
                    enableDarkTheme={enableDarkTheme}
                  />
                </Box>
              </Base>
              <Box
                sx={{
                  marginY: '20px',
                  backgroundColor: enableDarkTheme
                    ? palette.toolBarBackground
                    : palette.toolBarBackground,
                }}
              >
                <Tabs>
                  <TabList>
                    <ToolbarContainer
                      style={{
                        paddingBottom: 0,
                      }}
                      enableDarkTheme={enableDarkTheme}
                    >
                      {/* BUG: The Permission tag causes MUI to detect the SWTab and create empty tab indexes */}
                      <SWTab enableDarkTheme={enableDarkTheme}>
                        {t('component_cost_breakdown')}
                      </SWTab>
                      <SWTab enableDarkTheme={enableDarkTheme}>
                        {t('labour_cost_vs_part_cost')}
                      </SWTab>
                      <SWTab enableDarkTheme={enableDarkTheme}>
                        {t('maintenance_comparison')}
                      </SWTab>
                      <SWTab enableDarkTheme={enableDarkTheme}>
                        {t('maintenance_distribution')}
                      </SWTab>
                      <SWTab enableDarkTheme={enableDarkTheme}>
                        {t('work_order_status')}
                      </SWTab>
                    </ToolbarContainer>
                  </TabList>
                  <TabPanel
                    style={{
                      padding: '16px',
                    }}
                  >
                    {selectedCell.length === 0 ? (
                      <FallbackForNoSelection />
                    ) : (
                      <ComponentsCostBreakdown selectedCell={selectedCell} />
                    )}
                  </TabPanel>
                  <TabPanel
                    style={{
                      padding: '16px',
                    }}
                  >
                    {selectedCell.length === 0 ? (
                      <FallbackForNoSelection />
                    ) : (
                      <TotalCostVSLabourCost selectedCell={selectedCell} />
                    )}
                  </TabPanel>
                  <TabPanel>
                    {selectedCell.length === 0 ? (
                      <FallbackForNoSelection />
                    ) : (
                      <MaintenanceComparisonTab selectedCell={selectedCell} />
                    )}
                  </TabPanel>
                  <TabPanel>
                    {selectedCell.length === 0 ? (
                      <FallbackForNoSelection />
                    ) : (
                      <MaintenanceBreakdownTab selectedCell={selectedCell} />
                    )}
                  </TabPanel>
                  <TabPanel>
                    {selectedCell.length === 0 ? (
                      <FallbackForNoSelection />
                    ) : (
                      <WorkOrderStatusBreakdownTab
                        selectedCell={selectedCell}
                      />
                    )}
                  </TabPanel>
                </Tabs>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};
